import { faChevronCircleDown, faChevronCircleUp, faInfoCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, ButtonBase, Collapse, darken, lighten, useTheme } from "@mui/material";
import { InputComponentType, OmitInputProps } from "@ngt/forms-core";
import { GroupedField, GroupedFieldLabel, IGroupedFieldProps } from "@ngt/opms";
import { isString } from "lodash-es";
import { ComponentProps, ComponentType, createContext, PropsWithChildren, ReactElement, Dispatch, SetStateAction, useState, useMemo, useContext, FC } from "react";


interface IExpandableContext {
    WrappingComponent?: ComponentType<PropsWithChildren>;
    expandableContent?: ReactElement;
    expanded: boolean;
    setExpanded: Dispatch<SetStateAction<boolean>>;
}

const ExpandableContext = createContext<IExpandableContext>({
    expanded: false,
    setExpanded: () => {}
})

const ExpandingWrapper: FC<PropsWithChildren> = ({
    children
}) => {
    const { WrappingComponent, expandableContent, expanded } = useContext(ExpandableContext);

    let internal = children;

    if (WrappingComponent) {
        internal =(
            <WrappingComponent>
                {internal}
            </WrappingComponent>
        );
    }

    return (
        <>
            {internal}
            <Collapse in={expanded}>
                <Box sx={{ p: 2 }}>
                    {expandableContent}
                </Box>
            </Collapse>
        </>
    )
} 

export interface IGroupedExpandableFieldProps<
    TComponent extends InputComponentType = any,
    TValue = any,
    TForm extends object = any
> extends IGroupedFieldProps<TComponent, TValue, TForm> {
    expandableContent?: ReactElement
}

const GroupedExpandableFieldInternal = <
    TComponent extends InputComponentType = any,
    TValue = any,
    TForm extends object = any
>({
    expandableContent,
    WrappingComponent,
    LabelProps: { label, simpleLabel } = {},
    ...props
}: IGroupedExpandableFieldProps<TComponent, TValue, TForm> & Partial<OmitInputProps<ComponentProps<TComponent>>>) => {

    const theme = useTheme();

    const styles = useMemo(() => {
        const getColor = theme.palette.mode === 'light' ? darken : lighten;
        const getBackgroundColor = theme.palette.mode === 'light' ? lighten : darken;
        const getBorderColor = theme.palette.mode === 'light' ? lighten : darken;

        return {
            icon: {
                width: 32,
                minWidth: 32,
                minHeight: 32,
                height: 32,
                padding: theme.spacing(1, 1),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                borderWidth: 1,
                borderStyle: 'solid',
                '& > *': {
                    opacity: 0.9
                },
                '& :nth-of-type(2)': {
                    display: 'none'
                },
                '&:hover > :nth-of-type(1)': {
                    display: 'none'
                },
                '&:hover > :nth-of-type(2)': {
                    display: 'block'
                }
            },
            notes: {
                color: getColor(theme.palette.info.main, 0.6),
                backgroundColor: getBackgroundColor(theme.palette.info.main, 0.9),
                borderColor: getBorderColor(theme.palette.info.main, 0.8),
                '& > *': {
                    color: theme.palette.info.main,
                }
            }
        }
    }, [theme]);

    const [expanded, setExpanded] = useState(false);

    const context = useMemo(() => {
        return {
            WrappingComponent,
            expandableContent,
            expanded,
            setExpanded
        }
    }, [WrappingComponent, expandableContent, expanded, setExpanded])

    return (
        <ExpandableContext.Provider value={context as any}>
            <GroupedField 
                WrappingComponent={ExpandingWrapper}
                LabelProps={{
                    hide: false,
                    simpleLabel: false,
                    label: (
                        
                        <Box
                        sx={{
                            display: 'flex'
                        }}
                    >
                        <Box
                            sx={{
                                flex: '1 1 auto'
                            }}
                        >
                            {
                                label === undefined || isString(label) ?
                                    <GroupedFieldLabel 
                                        label={label} 
                                        simpleLabel={simpleLabel}
                                    /> :
                                    label
                            }
                        </Box>
                        <Box
                            sx={{
                                flex: '0 0 auto',
                                minWidth: 32,
                                maxWidth: 32
                            }}
                        >
                            <ButtonBase
                                onClick={() => { setExpanded(c => !c)}}
                                sx={{ ...styles.icon, ...styles.notes }}
                            >
                                <FontAwesomeIcon icon={faInfoCircle} fixedWidth />
                                <FontAwesomeIcon icon={expanded ? faChevronCircleUp : faChevronCircleDown} fixedWidth />
                            </ButtonBase>
                        </Box>
                    </Box>
                    )
                }}
                {...props} 
            />
        </ExpandableContext.Provider>
    )
}

const GroupedExpandableField = <
    TComponent extends InputComponentType = any,
    TValue = any,
    TForm extends object = any
>({
    expandableContent,
    ...props
}: IGroupedExpandableFieldProps<TComponent, TValue, TForm> & Partial<OmitInputProps<ComponentProps<TComponent>>>) => {
    
    if (!expandableContent || props.LabelProps?.hide) {
        return <GroupedField {...props} />;
    }

    return <GroupedExpandableFieldInternal {...props} expandableContent={expandableContent} />;
}

export default GroupedExpandableField;
